<template>
    <Modal2 v-if="visible" :showFooter="false" :showTitle="false" @cancel="$emit('close')"
        class="cancel-plan-resources-modal">
        <div class="resources-container">
            <!-- Başlık -->
            <h3 class="modal-title">{{ modalTitle }}</h3>

            <!-- Bilgi Mesajı -->
            <p class="alert-text">{{ infoMessage }}</p>

            <!-- Süpsis ise kullanıcı listesi -->
            <template v-if="isSupsis">
                <div v-if="users.length" class="list">
                    <div class="list-item" v-for="user in users" :key="user._id">
                        <div class="item-info">
                            <span class="item-name">{{ user.name }}</span>
                            <span class="item-email">{{ user.email }}</span>
                        </div>
                        <div class="item-actions">
                            <SupsisButton size="small" danger @click="onDeleteUser(user)" :icon="'delete'" />
                            <SupsisButton size="small" ghost @click="onDeactivateUser(user)"
                                :icon="user.enable ? 'radio_button_checked' : 'radio_button_unchecked'">
                                {{ user.enable ? 'Aktif' : 'Pasif' }}
                            </SupsisButton>
                        </div>
                    </div>
                </div>
                <div v-else class="no-item-text">Silinebilir bir kullanıcı bulunamadı.</div>
            </template>

            <!-- Kanal bazlı planlar -->
            <template v-else>
                <div v-if="channelsList.length" class="list">
                    <div class="list-item" v-for="channel in channelsList" :key="channel._id">
                        <div class="item-info">
                            <span class="item-name">{{ channel.name || formatChannelType(channel.type) }}</span>
                            <span class="item-type">{{ formatChannelType(channel.type) }}</span>
                        </div>
                        <div class="item-actions">
                            <SupsisButton size="small" danger @click="onDeleteChannel(channel)" :icon="'delete'" />
                        </div>
                    </div>
                </div>
                <div v-else class="no-item-text">Silinebilir bir kanal bulunamadı.</div>
            </template>
        </div>
    </Modal2>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import SupsisButton from '../../views/Pages/Settings/components/SupsisButton.vue';
import Modal2 from '../common/Modal2.vue';

export default {
    name: 'CancelPlanResourcesModal',
    components: {
        SupsisButton,
        Modal2,
    },
    props: {
        visible: {
            type: Boolean,
            default: false,
        },
        plan: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            deletingIds: new Set(),
        };
    },
    computed: {
        isSupsis() {
            return this.plan.type === 'supsis';
        },
        modalTitle() {
            return this.isSupsis ? 'Kullanıcıları Yönet' : 'Kanalları Yönet';
        },
        infoMessage() {
            return this.isSupsis
                ? 'Planınızı iptal edebilmek için birkaç kullanıcıyı silmeniz veya devre dışı bırakmanız gerekmektedir.'
                : 'Paketi iptal edebilmeniz için kanalları silmeniz gerekmektedir.';
        },
        ...mapState({
            usersState: state => state.user.users, // assumed structure
            channels: state => state.channel.channels,
            siteId: state => state.site.site?._id,
        }),
        loggedUser() {
            return this.$store.state.auth.user || {};
        },
        users() {
            // Silinebilir kullanıcılar: aktif/pasif fark etmez, kendisi ve sahip olanlar hariç
            return (this.usersState?.data || []).filter(
                u => u._id !== this.loggedUser._id || !u.isOwner
            );
        },
        channelsList() {
            const type = this.plan.type;
            const typeMap = {
                whatsapp: ['whatsapp-qr', 'whatsapp-v2', 'whatsapp-unipile'],
                'whatsapp-business': ['whatsapp-business-api'],
                instagram: ['instagram'],
                messenger: ['messenger'],
                'meta-lead': ['meta-leads'],
                trendyol: ['trendyol'],
                telegram: ['telegram-unipile'],
                linkedin: ['linkedin-unipile'],
                "voice-agent": ['retell'],
                "artificial-intelligence": ['openai'],
                "webphone": ['webphone-kirmizisantral', 'webphone-telnyx'],
            };
            const allowed = typeMap[type] || [];
            return this.channels.filter(c => allowed.includes(c.type));
        },
        userColumns() {
            return [
                { field: 'name', key: 'name', title: this.$t('message.username') },
                { field: 'email', key: 'email', title: this.$t('message.email') },
                {
                    field: 'actions',
                    key: 'actions',
                    title: this.$t('message.transactions'),
                    align: 'right',
                    renderBodyCell: ({ row }) => {
                        return (
                            <div class="d-flex gap-2 justify-content-end">
                                <SupsisButton size="small" danger onClick={() => this.onDeleteUser(row)}>
                                    {this.$t('message.delete')}
                                </SupsisButton>
                                <SupsisButton size="small" ghost onClick={() => this.onDeactivateUser(row)}>
                                    {row.enable ? this.$t('message.passive') : this.$t('message.active')}
                                </SupsisButton>
                            </div>
                        );
                    },
                },
            ];
        },
        channelColumns() {
            return [
                { field: 'name', key: 'name', title: this.$t('message.channelName') || 'Name', renderBodyCell: ({ row }) => row.name || row.type },
                { field: 'type', key: 'type', title: this.$t('message.channelType') || 'Type', renderBodyCell: ({ row }) => row.type },
                {
                    field: 'actions',
                    key: 'actions',
                    title: this.$t('message.transactions'),
                    align: 'right',
                    renderBodyCell: ({ row }) => {
                        return (
                            <div class="d-flex justify-content-end">
                                <SupsisButton size="small" danger onClick={() => this.onDeleteChannel(row)}>
                                    {this.$t('message.delete')}
                                </SupsisButton>
                            </div>
                        );
                    },
                },
            ];
        },
    },
    methods: {
        ...mapActions({
            deleteChannel: 'channel/deleteChannel',
            getChannels: 'channel/getChannels',
            getUsers: 'user/getUsers',
            updateUser: 'user/updateUser',
            deleteUserAction: 'user/deleteUser',
        }),
        formatChannelType(type) {
            return type.replace(/-/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
        },
        fetchUsers() {
            if (this.isSupsis) {
                this.getUsers({ perPage: 0, page: 1, updateStore: false });
            }
        },
        onDeleteUser(user) {
            this.$dialog.confirm('Kullanıcıyı silmek istediğinize emin misiniz?', {
                okText: 'Evet',
                cancelText: 'Hayır',
                customClass: 'supsis-dialog',
            }).then(() => {
                this.deleteUserAction({ id: user._id }).then(() => {
                    this.fetchUsers();
                });
            });
        },
        onDeactivateUser(user) {
            const newEnable = !user.enable;
            this.$dialog.confirm('Kullanıcıyı güncellemek istediğinize emin misiniz?', {
                okText: 'Evet',
                cancelText: 'Hayır',
                customClass: 'supsis-dialog',
            }).then(() => {
                this.updateUser({ user: { ...user, enable: newEnable }, siteId: this.siteId }).then(() => {
                    this.fetchUsers();
                });
            });
        },
        onDeleteChannel(channel) {
            this.$dialog.confirm('Kanalı silmek istediğinize emin misiniz?', {
                okText: 'Evet',
                cancelText: 'Hayır',
                customClass: 'supsis-dialog',
            }).then(() => {
                this.deleteChannel(channel._id).then(() => {
                    this.getChannels();
                });
            });
        },
    },
    watch: {
        visible(val) {
            if (val) this.fetchUsers();
        },
        channels(val) {
            if (this.channelsList.length === 0) {
                this.$emit('close');
            }
        },
    },
};
</script>

<style scoped>
.resources-container {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.modal-title {
    font-size: 18px;
    font-weight: 600;
    margin: 0;
}

.alert-text {
    background: #fff7e6;
    border: 1px solid #ffe58f;
    padding: 10px 12px;
    border-radius: 6px;
    font-size: 14px;
    color: #664d03;
}

.list {
    display: flex;
    flex-direction: column;
    gap: 10px;
    max-height: 400px;
    overflow-y: auto;
}

.list-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px;
    border: 1px solid #eaeaea;
    border-radius: 6px;
    background: #fff;
}

.item-info {
    display: flex;
    flex-direction: column;
}

.item-name {
    font-weight: 600;
    color: #222;
}

.item-email,
.item-type {
    font-size: 12px;
    color: #666;
}

.item-actions {
    display: flex;
    gap: 6px;
}
</style>
